import type * as THREE from 'three';
import type { RigSize } from '../config/character-roster';
import { createCharacterPackCache } from './character-pack-cache';

/**
 * Loads and caches frontend/public/assets/skeletons/ (design/gdd/skeleton-ai.md's
 * monster models) — same shape as adventurers-cache.ts, both delegating to
 * character-pack-cache.ts's generic loader.
 */

/**
 * `undead` on the medium rig only: `minion`/`warrior` are skeletons and get
 * the pack's own Skeletons_* idle/walk/death/awaken clips, while `golem` (the
 * Bone Warden) is a large-rig body the KayKit pack ships no undead set for.
 */
const cache = createCharacterPackCache('/assets/skeletons', { medium: ['undead'] });

export function loadCharacterModel(modelName: string): Promise<THREE.Group> {
  return cache.loadCharacterModel(modelName);
}

export function loadEquipmentModel(modelName: string): Promise<THREE.Group> {
  return cache.loadEquipmentModel(modelName);
}

/** Monsters wear no gear ladder — exposed only to satisfy the shared CharacterPackCache interface, same as `loadSkinTexture` below. */
export function loadGearModel(defId: string): Promise<THREE.Group> {
  return cache.loadGearModel(defId);
}

export function loadRigAnimations(rig: RigSize): Promise<THREE.AnimationClip[]> {
  return cache.loadRigAnimations(rig);
}

/** Monsters never carry a `skinAssetKey` (design/gdd/cosmetics-store.md is player-only) — exposed only to satisfy the shared CharacterPackCache interface. */
export function loadSkinTexture(skinAssetKey: string): Promise<THREE.Texture> {
  return cache.loadSkinTexture(skinAssetKey);
}

export function loadAdditiveClip(
  rig: string, sourceClipName: string, referenceClipName?: string,
): Promise<THREE.AnimationClip> {
  return cache.loadAdditiveClip(rig, sourceClipName, referenceClipName);
}

export function loadEmoteClips(rig: RigSize): Promise<THREE.AnimationClip[]> {
  return cache.loadEmoteClips(rig);
}
